import { notification } from 'antd';
import type { NotificationType, NotificationLink } from '../types/notification.types';
import { useAppStore } from '../stores/useAppStore';

interface NotificationHistoryOptions {
  persistToHistory?: boolean;
  historyType?: NotificationType;
  historyTitle?: string;
  metadata?: Record<string, unknown>;
  link?: NotificationLink;
}

function persistNotificationToHistory(options: {
  type: NotificationType;
  title: string;
  message: string;
  metadata?: Record<string, unknown>;
  link?: NotificationLink;
}): void {
  useAppStore.getState().addNotification({
    type: options.type,
    title: options.title,
    message: options.message,
    metadata: options.metadata,
    link: options.link,
  });
}

function maybePersistHistory(
  options: NotificationHistoryOptions | undefined,
  fallbackType: NotificationType,
  title: string,
  message: string
): void {
  if (!options?.persistToHistory) return;

  persistNotificationToHistory({
    type: options.historyType ?? fallbackType,
    title: options.historyTitle ?? title,
    message,
    metadata: options.metadata,
    link: options.link,
  });
}

/**
 * Унифицированная система уведомлений приложения.
 * 
 * Все уведомления отображаются в левом верхнем углу экрана.
 * Использует Ant Design notification API.
 * 
 * @example
 * ```typescript
 * import { appNotify } from '@/utils/notifications';
 * 
 * // Успешное уведомление
 * appNotify.success('Сохранено', 'Данные успешно сохранены');
 * 
 * // Ошибка
 * appNotify.error('Ошибка сохранения', errorMessage);
 * 
 * // Нет изменений для сохранения
 * appNotify.noChanges();
 * 
 * // Предупреждение о незаполненных строках
 * appNotify.emptyRows(3);
 * ```
 */
export const appNotify = {
  /**
   * Уведомление об успехе
   * @param message - Заголовок уведомления
   * @param description - Описание (опционально)
   */
  success: (message: string, description?: string, options?: NotificationHistoryOptions) => {
    const title = message;
    const detail = description ?? '';
    maybePersistHistory(options, 'system', title, detail || title);
    return notification.success({
      message,
      description,
      placement: 'topLeft',
      duration: 4,
    });
  },

  /**
   * Уведомление об ошибке
   * @param message - Заголовок уведомления
   * @param description - Описание ошибки (опционально)
   */
  error: (message: string, description?: string, options?: NotificationHistoryOptions) => {
    const title = message;
    const detail = description ?? '';
    maybePersistHistory(options, 'error', title, detail || title);
    return notification.error({
      message,
      description,
      placement: 'topLeft',
      duration: 8,
    });
  },

  /**
   * Информационное уведомление
   * @param message - Заголовок уведомления
   * @param description - Описание (опционально)
   */
  info: (message: string, description?: string, options?: NotificationHistoryOptions) => {
    const title = message;
    const detail = description ?? '';
    maybePersistHistory(options, 'system', title, detail || title);
    return notification.info({
      message,
      description,
      placement: 'topLeft',
      duration: 4,
    });
  },

  /**
   * Предупреждающее уведомление
   * @param message - Заголовок уведомления
   * @param description - Описание (опционально)
   */
  warning: (message: string, description?: string, options?: NotificationHistoryOptions) => {
    const title = message;
    const detail = description ?? '';
    maybePersistHistory(options, 'system', title, detail || title);
    return notification.warning({
      message,
      description,
      placement: 'topLeft',
      duration: 6,
    });
  },

  /**
   * Стандартное уведомление "Нет изменений для сохранения"
   */
  noChanges: (options?: NotificationHistoryOptions) => {
    const title = 'Нет изменений';
    const detail = 'Нет изменений для сохранения';
    maybePersistHistory(options, 'system', title, detail);
    return notification.info({
      message: 'Нет изменений',
      description: 'Нет изменений для сохранения',
      placement: 'topLeft',
      duration: 4,
    });
  },

  /**
   * Уведомление о незаполненных строках
   * @param count - Количество незаполненных строк
   */
  emptyRows: (count: number, options?: NotificationHistoryOptions) => {
    const title = 'Незаполненные строки';
    const detail = `${count} незаполненных строк не будут сохранены. Заполните обязательные поля и сохраните снова.`;
    maybePersistHistory(options, 'system', title, detail);
    return notification.warning({
      message: 'Незаполненные строки',
      description: detail,
      placement: 'topLeft',
      duration: 6,
    });
  },

  /**
   * Уведомление об обновлении данных
   * @param count - Количество загруженных записей
   */
  dataReloaded: (count: number, options?: NotificationHistoryOptions) => {
    const title = 'Данные обновлены';
    const detail = `Загружено записей: ${count}`;
    maybePersistHistory(options, 'system', title, detail);
    return notification.success({
      message: 'Данные обновлены',
      description: detail,
      placement: 'topLeft',
      duration: 3,
    });
  },

  /**
   * Уведомление об ошибке загрузки данных
   */
  reloadError: (options?: NotificationHistoryOptions) => {
    const title = 'Ошибка обновления';
    const detail = 'Не удалось загрузить данные. Попробуйте еще раз.';
    maybePersistHistory(options, 'error', title, detail);
    return notification.error({
      message: 'Ошибка обновления',
      description: detail,
      placement: 'topLeft',
      duration: 5,
    });
  },

  /**
   * Уведомление об ошибке сохранения данных
   * @param description - Описание ошибки
   */
  saveError: (description?: string, options?: NotificationHistoryOptions) => {
    const title = 'Ошибка сохранения данных';
    const detail = description ?? '';
    maybePersistHistory(options, 'error', title, detail || title);
    return notification.error({
      message: 'Ошибка сохранения данных',
      description,
      placement: 'topLeft',
      duration: 8,
    });
  },
};


